import { el } from "../ui/dom";
import { signal, type Signal } from "../ui/signals";

/**
 * A change counter for imperative state. `bump()` advances a signal via a plain
 * counter — crucially it never *reads* the signal, so it is safe to call from
 * inside an effect without creating a self-dependency (which would loop).
 */
export function ticker(): { tick: Signal<number>; bump: () => void } {
  const tick = signal(0);
  let c = 0;
  return { tick, bump: () => (tick.value = ++c) };
}

/** Read a CSS custom property so canvas drawing can follow the active theme. */
export function cssVar(name: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || "#888888";
}

export interface ThemeColors {
  accent: string;
  accent2: string;
  border: string;
  borderSoft: string;
  bg2: string;
  surface: string;
  danger: string;
  warn: string;
  textFaint: string;
}

export function themeColors(): ThemeColors {
  return {
    accent: cssVar("--accent"),
    accent2: cssVar("--accent-2"),
    border: cssVar("--border"),
    borderSoft: cssVar("--border-soft"),
    bg2: cssVar("--bg-2"),
    surface: cssVar("--surface"),
    danger: cssVar("--danger"),
    warn: cssVar("--warn"),
    textFaint: cssVar("--text-faint"),
  };
}

/** A section heading used inside pages. */
export function h2(text: string): HTMLElement {
  return el("h2", { style: { fontSize: "1.35rem", letterSpacing: "-0.02em", margin: "38px 0 6px" } }, text);
}

/** A small legend row: pairs of [color, label]. */
export function legend(items: Array<[string, string]>): HTMLElement {
  return el(
    "div",
    { class: "legend-row" },
    ...items.map(([color, label]) =>
      el("span", {}, el("span", { class: "swatch", style: { background: color } }), label),
    ),
  );
}

/** A caption under a visualization. */
export function caption(text: string): HTMLElement {
  return el("div", { class: "viz-caption" }, text);
}
